import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
} from '@heroicons/react/24/solid';
import Modal from '../Modal';
import './profileImageModal.css';
import ImageCrop from '../ImageCrop';
import Button from '../Button';
import Spinner from '../Spinner';
import { FileInput } from '../FileUpload';

const ProfileImageModal = ({ children, onChange, onClose, loading, allowedFileTypes }) => {
  const [src, setSrc] = useState(null);
  const [cropping, setCropping] = useState(null);
  const { t } = useTranslation('components');
  const cropperRef = useRef();
  const inputRef = useRef();
  const fileFormRef = useRef();

  const handleFilesChanged = e => {
    const { target: input } = e;
    const [file] = input.files;
    setSrc(URL.createObjectURL(file));
  };

  const reset = () => {
    setSrc(null);
    if (fileFormRef.current) fileFormRef.current.reset();
  };

  const handleCrop = async e => {
    e.preventDefault();
    setCropping(true);
    const { current: cropper } = cropperRef;
    onChange({ avatar: await cropper.toBlob() });
    setSrc(null);
    setCropping(false);
  };

  return (
    <Modal showCloseButton onClickCloseButton={onClose} onClickOutside={onClose}>
      <div className="p-4">
        <header>
          <h2 className="text-2xl font-semibold mb-4">{t('ProfileImageModal.title')}</h2>
        </header>
        <div className="flex flex-col">
          {src ? (
            <form className="flex flex-col flex-auto" onSubmit={handleCrop} onReset={reset}>
              <ImageCrop
                className="flex-auto self-center mb-4"
                src={src}
                ref={cropperRef}
                style={{ maxWidth: '500px', maxHeight: '500px' }}
              />
              <div className="flex flex-row space-x-1 self-center mb-8">
                <Button
                  kind="secondary"
                  shape="square"
                  size="xs"
                  onClick={() => cropperRef.current.move(-10, 0)}
                >
                  <ArrowLeftIcon className="w-4 h-4 font-semibold" />
                </Button>
                <Button
                  kind="secondary"
                  shape="square"
                  size="xs"
                  onClick={() => cropperRef.current.move(10, 0)}
                >
                  <ArrowRightIcon className="w-4 h-4 font-semibold" />
                </Button>
                <Button
                  kind="secondary"
                  shape="square"
                  size="xs"
                  onClick={() => cropperRef.current.move(0, -10)}
                >
                  <ArrowUpIcon className="w-4 h-4 font-semibold" />
                </Button>
                <Button
                  kind="secondary"
                  shape="square"
                  size="xs"
                  onClick={() => cropperRef.current.move(0, 10)}
                >
                  <ArrowDownIcon className="w-4 h-4 font-semibold" />
                </Button>
                <Button
                  kind="secondary"
                  shape="square"
                  size="xs"
                  onClick={() => cropperRef.current.zoom(-0.1)}
                >
                  <MagnifyingGlassMinusIcon className="w-4 h-4 font-semibold" />
                </Button>
                <Button
                  kind="secondary"
                  shape="square"
                  size="xs"
                  onClick={() => cropperRef.current.zoom(0.1)}
                >
                  <MagnifyingGlassPlusIcon className="w-4 h-4 font-semibold" />
                </Button>
              </div>
              <div className="flex self-center">
                <Button type="reset" kind="secondary" className="self-end">
                  {t('ProfileImageModal.cancel')}
                </Button>
                <Button type="submit" className="ml-2 self-end" isLoading={cropping}>
                  {t('ProfileImageModal.save')}
                </Button>
              </div>
            </form>
          ) : (
            <form ref={fileFormRef} className="flex-auto">
              <FileInput
                onChange={handleFilesChanged}
                ref={inputRef}
                accept={allowedFileTypes.join(',')}
              >
                {({ select }) => (
                  <>
                    {/* eslint-disable-next-line */}
                    <label className="cursor-pointer flex-auto flex flex-col items-center">
                      {!loading ? (
                        children
                      ) : (
                        <div className="w-full" style={{ maxWidth: '400px' }}>
                          <div
                            className="relative rounded-full border w-full focus:outline-none mb-8"
                            style={{
                              paddingTop: '100%',
                            }}
                          >
                            <div className="absolute flex top-0 left-0 bottom-0 right-0 w-full h-full justify-center items-center">
                              <Spinner size="2xl" />
                            </div>
                          </div>
                        </div>
                      )}
                      <Button onClick={select}>{t('ProfileImageModal.choose')}</Button>
                    </label>
                  </>
                )}
              </FileInput>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
};

ProfileImageModal.defaultProps = {
  onChange: () => {},
  onClose: () => {},
  loading: false,
  allowedFileTypes: [],
};

ProfileImageModal.propTypes = {
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  loading: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  allowedFileTypes: PropTypes.arrayOf(PropTypes.string),
};

export default ProfileImageModal;
